import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React from 'react';

import DeleteIconButton from './DeleteIconButton';
import { featureFlagColumns } from './TagCategoryTableView';

// CP-7968: a trip's own tag category rows next to the Tour Operator defaults (Allison's
// "compare" view). Rows that apply to the trip are green, rows that don't are grey, and a
// setting where the two rows of a category differ is highlighted in both. Unlike her mockup,
// the trip's rows are never "dormant": with the defaults on, a trip row still wins over the
// default for its category (backend get_effective_tag_categories), and the defaults only fill
// in the categories the trip has no row for. Read-only apart from deleting a trip row.

export const COMPARE_STATUS_LABELS = {
  same: 'Same as default',
  differs: 'Differs from default',
  trip_only: 'Only on this trip',
  default_only: 'Only in defaults',
};

const STATUS_ORDER = ['same', 'differs', 'trip_only', 'default_only'];

const STATUS_CHIP_COLORS = {
  same: 'success',
  differs: 'warning',
  trip_only: 'primary',
  default_only: 'default',
};

const APPLIES_BG = '#e8f5e9';
const NOT_USED_BG = '#f5f5f5';
const DIFFERS_BG = '#ffe0b2';

const useStyles = makeStyles(() => ({
  tableContainer: {
    maxHeight: 600,
    '& .MuiTableCell-root': {
      padding: '4px 8px',
      fontSize: '0.75rem',
    },
  },
  headerCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  categoryCell: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    minWidth: 160,
    backgroundColor: 'white',
    borderRight: '1px solid #e0e0e0',
    verticalAlign: 'top',
  },
  statusCell: {
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },
  appliesRow: {
    backgroundColor: APPLIES_BG,
  },
  notUsedRow: {
    backgroundColor: NOT_USED_BG,
    '& $levelCell, & $checkboxCell': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
  },
  levelCell: {
    whiteSpace: 'nowrap',
  },
  checkboxCell: {
    textAlign: 'center',
  },
  differsCell: {
    backgroundColor: DIFFERS_BG,
  },
}));

const columnValue = (col, row) => (col.getValue ? col.getValue(row) : !!row[col.id]);

// Why a default row does not apply to the trip, or null when it does.
function defaultNotUsedReason(entry, usesOperatorDefaults) {
  if (!usesOperatorDefaults) return "This trip doesn't use the Tour Operator defaults";
  if (entry.trip) return "This trip's own row is used instead";
  return null;
}

function SettingsCells({ row, otherRow, classes }) {
  return featureFlagColumns.map((col) => {
    const value = columnValue(col, row);
    const differs = !!otherRow && value !== columnValue(col, otherRow);
    return (
      <TableCell
        key={col.id}
        className={`${classes.checkboxCell} ${differs ? classes.differsCell : ''}`}
        data-differs={differs ? 'true' : undefined}
      >
        <Checkbox size="small" checked={value} disabled sx={{ padding: '2px' }} inputProps={{ 'aria-label': col.label }} />
      </TableCell>
    );
  });
}

const TagCategoryCompareTable = ({ rows, usesOperatorDefaults, canDelete, onDelete }) => {
  const classes = useStyles();

  const counts = STATUS_ORDER.map((status) => ({
    status,
    count: rows.filter((entry) => entry.status === status).length,
  }));

  return (
    <Box>
      <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
        {usesOperatorDefaults
          ? 'Green rows apply to this trip. Where this trip has its own row for a tag category, that row is used instead of the default. A trip row that is the same as the default changes nothing today, but the trip keeps its own copy when the default changes: delete it to follow the default.'
          : "This trip doesn't use the Tour Operator defaults: only its own rows (green) apply. Turned on, the defaults would add the categories this trip has no row for; the trip's own rows would still be used instead of the defaults."}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
        {counts.map(({ status, count }) => (
          <Chip
            key={status}
            size="small"
            variant="outlined"
            color={STATUS_CHIP_COLORS[status]}
            label={`${COMPARE_STATUS_LABELS[status]}: ${count}`}
          />
        ))}
      </Box>

      {rows.length === 0 ? (
        <Typography style={{ fontSize: '90%', marginBottom: '1rem', paddingLeft: '1rem' }}>
          No tag categories on this trip or in the Tour Operator defaults.
        </Typography>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small" aria-label="Tag categories compared with the Tour Operator defaults">
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.headerCell} ${classes.categoryCell}`}>Category</TableCell>
                <TableCell className={classes.headerCell}>Compared with default</TableCell>
                <TableCell className={classes.headerCell}>Row</TableCell>
                {featureFlagColumns.map((col) => (
                  <TableCell key={col.id} align="center" className={classes.headerCell}>
                    <Tooltip title={col.tooltip}>
                      <span>{col.label}</span>
                    </Tooltip>
                  </TableCell>
                ))}
                <TableCell className={classes.headerCell} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((entry) => {
                const rowSpan = entry.trip && entry.def ? 2 : 1;
                const notUsedReason = entry.def ? defaultNotUsedReason(entry, usesOperatorDefaults) : null;
                const lead = (
                  <>
                    <TableCell rowSpan={rowSpan} className={classes.categoryCell}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {entry.category?.name || 'Unknown Category'}
                      </Typography>
                    </TableCell>
                    <TableCell rowSpan={rowSpan} className={classes.statusCell}>
                      <Chip
                        size="small"
                        color={STATUS_CHIP_COLORS[entry.status]}
                        label={COMPARE_STATUS_LABELS[entry.status]}
                        data-status={entry.status}
                      />
                    </TableCell>
                  </>
                );
                return (
                  <React.Fragment key={entry.key}>
                    {entry.trip && (
                      <TableRow className={classes.appliesRow} data-row="trip">
                        {lead}
                        <TableCell className={classes.levelCell} data-level>
                          This trip
                        </TableCell>
                        <SettingsCells row={entry.trip} otherRow={entry.def} classes={classes} />
                        <TableCell align="center">
                          {onDelete && entry.trip.idx != null && (
                            <DeleteIconButton
                              allowed={canDelete}
                              title={entry.status === 'same' && usesOperatorDefaults ? 'Delete: the trip then follows the default' : 'Delete'}
                              onClick={() => onDelete(entry.trip.idx)}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                    {entry.def && (
                      <TableRow className={notUsedReason ? classes.notUsedRow : classes.appliesRow} data-row="default">
                        {!entry.trip && lead}
                        <TableCell className={classes.levelCell} data-level>
                          {notUsedReason ? (
                            <Tooltip title={notUsedReason}>
                              <span>Default · not used</span>
                            </Tooltip>
                          ) : (
                            'Default'
                          )}
                        </TableCell>
                        <SettingsCells row={entry.def} otherRow={entry.trip} classes={classes} />
                        <TableCell />
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

SettingsCells.propTypes = {
  row: PropTypes.object.isRequired,
  otherRow: PropTypes.object,
  classes: PropTypes.object.isRequired,
};

TagCategoryCompareTable.propTypes = {
  // compareTagCategoriesWithDefaults (utils/tagCategories.js)
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      category: PropTypes.object,
      trip: PropTypes.object,
      def: PropTypes.object,
      status: PropTypes.oneOf(STATUS_ORDER).isRequired,
    })
  ).isRequired,
  usesOperatorDefaults: PropTypes.bool,
  canDelete: PropTypes.bool,
  onDelete: PropTypes.func,
};

export default TagCategoryCompareTable;
